import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";

const ACTIVITY_ROUTES = [
  { match: /^\/(comptoir|cartes-cadeaux)/, activity: "comptoir" },
  { match: /^\/services/, activity: "services" },
  { match: /^\/$/, activity: "univers" },
  { match: /^\//, activity: "palais" },
];

export const DEFAULT_THEME = {
  copper: "#C4704F",
  copperdark: "#A85B3D",
  gold: "#C6A87C",
  ink: "#050505",
  coal: "#0F0F0F",
  ivory: "#F5F5F0",
  radius: "0.5rem",
  font: "Montserrat",
};

const GLOBAL_SELECTORS = {
  "global.btn_primary": ".btn-primary",
  "global.btn_outline": ".btn-outline",
  "global.btn_gold": ".btn-gold",
  "global.form_input": ".input-dark",
};

const hexToRgb = (hex) => {
  const m = (hex || "").replace("#", "");
  const v = m.length === 3 ? m.split("").map((c) => c + c).join("") : m;
  const n = parseInt(v, 16);
  if (Number.isNaN(n)) return null;
  return `${(n >> 16) & 255} ${(n >> 8) & 255} ${n & 255}`;
};

const SHADOWS = {
  sm: "0 2px 8px rgba(0,0,0,0.3)",
  md: "0 8px 24px rgba(0,0,0,0.45)",
  lg: "0 16px 48px rgba(0,0,0,0.6)",
};

const selectorFor = (key) => GLOBAL_SELECTORS[key] || `[data-el="${key}"]`;

const BASE_TOKEN_KEYS = ["copper", "copperdark", "gold", "ink", "coal", "ivory", "radius", "font"];

const pickBaseTokens = (design) => {
  const out = {};
  for (const k of BASE_TOKEN_KEYS) {
    if (design[k]) out[k] = design[k];
  }
  return out;
};

const ensureFontLoaded = (font) => {
  if (!font || font === "Montserrat") return;
  const id = `font-${font.replace(/\s+/g, "-")}`;
  if (document.getElementById(id)) return;
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?family=${font.replace(/ /g, "+")}:wght@400;500;600;700&display=swap`;
  document.head.appendChild(link);
};

const buildElementCss = (elementStyles) => {
  let css = "";
  let needsKeyframes = false;
  for (const [key, s] of Object.entries(elementStyles || {})) {
    const sel = selectorFor(key);
    const rules = [];
    if (s.color) rules.push(`color:${s.color}`);
    if (s.gradient && s.gradient_from && s.gradient_to) {
      rules.push(`background:linear-gradient(135deg,${s.gradient_from},${s.gradient_to})`);
    } else if (s.background) {
      rules.push(`background-color:${s.background}`);
    }
    if (s.border_color) rules.push(`border-color:${s.border_color}`);
    if (s.border_width) rules.push(`border-width:${s.border_width}px;border-style:solid`);
    if (s.radius) rules.push(`border-radius:${s.radius}`);
    if (s.shadow && s.shadow !== "none" && SHADOWS[s.shadow]) rules.push(`box-shadow:${SHADOWS[s.shadow]}`);
    if (s.opacity) rules.push(`opacity:${s.opacity}`);
    if (rules.length) css += `${sel}{${rules.join(";")}}\n`;

    const hover = [];
    if (s.hover === "lift") hover.push("transform:translateY(-3px)");
    if (s.hover === "glow") hover.push(`box-shadow:0 0 24px ${s.border_color || s.background || "#C6A87C"}66`);
    if (s.hover === "darken") hover.push("filter:brightness(0.85)");
    if (s.hover_color) hover.push(`color:${s.hover_color}`);
    if (s.hover_background) hover.push(`background-color:${s.hover_background}`);
    if (hover.length) css += `${sel}:hover{${hover.join(";")};transition:all .3s ease}\n`;

    if (s.animation === "pulse") {
      css += `${sel}{animation:el-pulse 2.4s ease-in-out infinite}\n`;
      needsKeyframes = true;
    }
    if (s.animation === "float") {
      css += `${sel}{animation:el-float 4s ease-in-out infinite}\n`;
      needsKeyframes = true;
    }
  }
  if (needsKeyframes) {
    css +=
      "@keyframes el-pulse{0%,100%{transform:scale(1)}50%{transform:scale(1.04)}}\n" +
      "@keyframes el-float{0%,100%{transform:translateY(0)}50%{transform:translateY(-6px)}}\n";
  }
  return css;
};

export default function ThemeEngine() {
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    api
      .get("/settings")
      .then((r) => {
        if (cancelled) return;
        const design = r.data?.design || {};
        const activity =
          ACTIVITY_ROUTES.find((a) => a.match.test(location.pathname))?.activity || "palais";
        const theme = {
          ...DEFAULT_THEME,
          ...pickBaseTokens(design),
          ...(design.themes?.[activity] || {}),
        };
        ensureFontLoaded(theme.font);
        const root = document.documentElement;
        const setVar = (name, hex) => {
          const rgb = hexToRgb(hex);
          if (rgb) root.style.setProperty(name, rgb);
        };
        setVar("--copper-rgb", theme.copper);
        setVar("--copperdark-rgb", theme.copperdark);
        setVar("--gold-rgb", theme.gold);
        setVar("--ink-rgb", theme.ink);
        setVar("--coal-rgb", theme.coal);
        setVar("--ivory-rgb", theme.ivory);
        root.style.setProperty("--radius", theme.radius || DEFAULT_THEME.radius);
        root.style.setProperty("--site-font", `'${theme.font || DEFAULT_THEME.font}', sans-serif`);

        let tag = document.getElementById("theme-engine-styles");
        if (!tag) {
          tag = document.createElement("style");
          tag.id = "theme-engine-styles";
          document.head.appendChild(tag);
        }
        tag.textContent = buildElementCss(design.element_styles);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  return null;
}
